if (!('process' in globalThis)) {
  (globalThis as any).process = { env: {} };
}

/**
 * Appconfig is defined by the dotnet backend when rendering a page.
 * See src/OCS.Web/Views/Shared/_AppConfig.cshtml
 *
 * Our goal is to eventually decouple the frontend/backend at which point
 * these process.env.APPCONFIG_* variables will be defined by a standalone
 * devserver or statically rendered within an nginx container.
 */
export const appconfig = {
  // isDebugBuild depends only on the NODE_ENV variable which is controlled
  // but esbuild (the bundler).
  // lots of libraries like React itself also depend on this NODE_ENV check
  // for code optimization.
  isDebugBuild: process.env.NODE_ENV !== 'production',
  idp: {
    authority: process.env.APPCONFIG_IDP_AUTHORITY ?? 'http://localhost:9000',
    client_id: process.env.APPCONFIG_IDP_CLIENT_ID ?? 'ep-web-frontend',
    redirect_uri: process.env.APPCONFIG_IDP_REDIRECT_URI ?? `${location.origin}/login/callback`,
    post_logout_redirect_uri: process.env.APPCONFIG_IDP_LOGOUT_URI ?? `${location.origin}/logout/callback`,
  },
  launchDarkley: {
    // Note that the launch darkley key is the client key
    // which is intended for use in a public website.
    // Please don't mistake this for the backend api key
    // which is intended for private backend use.
    key: process.env.APPCONFIG_LAUNCH_DARKLEY_KEY ?? '6417dfddd47caa12e265cb04',
  },
  web: {
    // We are a single origin website so the web endpoint must be the current domain.
    // The webserver must proxy API requests if required.
    endpoint: process.env.APPCONFIG_WEB_ENDPOINT ?? window.location.origin,
  },
  webapi: {
    endpoint: process.env.APPCONFIG_WEBAPI_ENDPOINT ?? `${window.location.origin}/-`,
  },
  payments: {
    endpoint: process.env.APPCONFIG_PAYMENTS_ENDPOINT ?? 'http://localhost:12000',
  },
  notifications: {
    endpoint: process.env.APPCONFIG_NOTIFICATIONS_ENDPOINT ?? 'http://localhost:11000',
  },
  cdn: {
    endpoint: process.env.APPCONFIG_CDN_ENDPOINT ?? 'https://ocsdev01.blob.core.windows.net',
  },
  metabase: {
    endpoint: process.env.APPCONFIG_METABASE_ENDPOINT ?? 'https://metabase.staging.cetdemo.com',
  },
  // NOTE: this braintree config is for the legacy payments implementation (being replaced with payments service)
  // NOTE: these keys are "tokenization" keys which are designed for use in a browser application to submit a payment form.
  // These are not private keys used by a server.
  braintree: {
    tokenizationKey: process.env.APPCONFIG_BRAINTREE_BRAINTREE_TOKENIZATION_KEY ?? 'sandbox_928q2ypf_cjt8fr74thppc2g7',
    tokenizationKeyUs:
      process.env.APPCONFIG_BRAINTREE_BRAINTREE_TOKENIZATION_KEY_US ?? 'sandbox_s9f3nf2q_pmhgxtvwgfc5gpsh',
    tokenizationKeyInformaPremier:
      process.env.APPCONFIG_BRAINTREE_BRAINTREE_TOKENIZATION_KEY_INFORMAPREMIER ?? 'sandbox_24w25yx6_fcdwbfqpcg35n5t7',
    tokenizationKeyInformaBoat:
      process.env.APPCONFIG_BRAINTREE_BRAINTREE_TOKENIZATION_KEY_INFORMABOAT ?? 'sandbox_24w25yx6_fcdwbfqpcg35n5t7',
    tokenizationKeyInformaArt:
      process.env.APPCONFIG_BRAINTREE_BRAINTREE_TOKENIZATION_KEY_INFORMAART ?? 'sandbox_24w25yx6_fcdwbfqpcg35n5t7',
  },
  /**
   * NOTE: the environment name config should not be used by new code.
   * feature detection should use config flags instead of hardcoded environment names.
   * @deprecated
   */
  legacyEnvironmentName: process.env.APPCONFIG_LEGACY_ENVIRONMENT_NAME ?? 'LOCAL',
};

// Adding "appconfig" to the window to simplify debugging.
(globalThis as any).appconfig = appconfig;

// Adding OCS.JsConstants to window because angularjs depends on it.
(globalThis as any).OCS = {
  JsConstants: {
    True: 'true',
    False: 'false',
    Application: {
      // TODO: app version is not really used (dead code)
      APP_VERSION: '1.0.0.0',
      // TODO: environment name is only really used for CN feature detection which is unused (dead code)
      ENVIRONMENT_NAME: appconfig.legacyEnvironmentName,
      Lang: 'en',
      AzureStoragePath: trailingSlash(appconfig.cdn.endpoint, true),
      SelectedLang: 'en',
      DashboardPendingRequestTimeout: '1400',
      BraintreeTokenizationKey: appconfig.braintree.tokenizationKey,
      BraintreeTokenizationKeyUs: appconfig.braintree.tokenizationKeyUs,
      BraintreeTokenizationKeyInformaPremier: appconfig.braintree.tokenizationKeyInformaPremier,
      BraintreeTokenizationKeyInformaBoat: appconfig.braintree.tokenizationKeyInformaBoat,
      BraintreeTokenizationKeyInformaArt: appconfig.braintree.tokenizationKeyInformaArt,
    },
    Product: {
      NAME: 'EventsPass',
      SUPPORT_EMAIL: 'support@eventspass.com',
      ATTENDEE_SUPPORT_EMAIL: 'attendeesupport@eventspass.com',
      PHONE_NUMBER: '+61 (3) 9988 1420',
      PHONE_NUMBER_USA: '+1 253 218 1890',
      PHONE_NUMBER_UK: '+44 1865 52 2412',
      URL: 'EventsPass.com',
      URL_WWW: 'https://www.eventspass.com',
      ABOUT_US: 'https://www.eventspass.co/about/',
      PRIVACY_POLICY: 'https://www.eventspass.co/privacy-policy/',
      TERMS_OF_USE: 'https://www.eventspass.co/terms-and-conditions-visitors-event-attendees/',
      TERMS_OF_USE_EO: 'https://www.eventspass.co/terms-and-conditions-event-organisers/',
      TERMS_OF_USE_EXIBITOR:
        'https://www.eventspass.co/terms-and-conditions-exhibitors-vendors-and-other-third-parties/',
      MULTI_CURRENCY_RATES: 'https://www.eventspass.co/terms-and-conditions-event-organisers/#feespayments',
      BTPN: 'EventsP',
      BTIPN: 'Informa',
      SUBMIT_QUERY_URL: 'https://www.eventspass.info/ticket-holder-purchaser-support/',
      METABASE_ENDPOINT: trailingSlash(appconfig.metabase.endpoint, false),
    },
    isInDebugMode: String(appconfig.isDebugBuild),
    // NOTE: these BaseUrl settings are used by angularjs for url navigation so these must always
    // point to the website itself and not the backend API in anyway.
    BaseUrl: {
      path: '',
      schemeAndAuthority: window.location.origin,
    },
    Web: {
      BaseUrl: trailingSlash(appconfig.web.endpoint, true),
    },
    WebBookADemoAddress: 'https://www.eventspass.info/#booknow',
    Api: {
      BaseUrl: trailingSlash(appconfig.webapi.endpoint, true),
    },
    // TODO: seems like dead code because this value was never configured.
    AgendaWeb: {
      BaseUrl: 'http://stagingapp.cloudapp.net/Agenda/',
    },
    OAuth: {
      BaseUrl: trailingSlash(appconfig.webapi.endpoint, true),
    },
    Image: {
      Url: {
        FLAGSPATH: new URL('/Content/Theme/global/img/flags/', appconfig.webapi.endpoint).toString(),
        LOGO_LOGIN: new URL('/Content/Theme/admin/layout/img/logo_login.png', appconfig.webapi.endpoint).toString(),
        LOGO: new URL('/Content/Theme/admin/layout/img/logo.png', appconfig.webapi.endpoint).toString(),
        LOGOZH: new URL('/Content/Theme/admin/layout/img/logozh.png', appconfig.webapi.endpoint).toString(),
        LOGIN_LOGO: new URL('/Content/Theme/admin/layout/img/login_logo.png', appconfig.webapi.endpoint).toString(),
      },
    },
    BlobImages: {
      DefaultDesign: `${trailingSlash(appconfig.cdn.endpoint, true)}ocs-public/DefaultAssets/Ticket_Front_Design.png`,
    },
    // TODO: probably dead code because it's not configured or referenced anywhere.
    Ticketing: {
      PurchaseLimit: '20',
    },
    // TODO: probably dead code because it's not configured or referenced anywhere.
    Remittance: {
      Percentage: '0.3',
    },
    // ErrorCode corresponds to the OCS.Core.Exceptions.ErrorCode enum.
    ErrorCode: {
      UnexpectedError: 1,
      UserNotFound: 100,
      UsernameAlreadyTaken: 101,
      NoUserExistsWithSpecifiedEmailAddress: 102,
      MobileNumberMismatch: 103,
      InvalidGrant: 104,
      UserNotActive: 105,
      EmailAddressAssignedToOtherUser: 106,
      InvalidClient: 107,
      InvalidProvider: 108,
      AccessDenied: 109,
      PinDoesNotExist: 110,
      PinExpired: 111,
      MoreRecentPinExistsForUser: 112,
      WrongCurrentPassword: 113,
      InvalidNewPassword: 114,
      SecurityCheckFailed: 115,
      TaxRulesExistWithNames: 200,
      CountryAlreadyMappedToDateTimeMapping: 201,
      BaseCurrencyAlreadyExists: 202,
      CompanyAlreadyExists: 203,
      LanguageAlreadyDefined: 204,
      BaseCurrencyInUse: 205,
      FailedProcessingPayment: 300,
      MarketplaceAlreadyHasTicketPackagePriceAssigned: 301,
      InsufficientQuantity: 400,
      InvalidPurchaseRequest: 500,
      InvalidTicketCode: 501,
      TicketAlreadyScanned: 502,
      TicketInvalidDay: 503,
      IncompleteRegistrations: 504,
      InvalidRemittanceRequest: 600,
      SoftPaymentErrorMessage: 1000,
      FailedProcessingPaymentBt: 1001,
      SoftPaymentCCNotSupportedErrorMessage: 1002,
      NotFound: 1003,
      InvalidArgument: 1004,
      FailedPrecondition: 1005,
      PermissionDenied: 1006,
      Unauthenticated: 1007,
    },
  },
  BrowserLocation: {},
};

function trailingSlash(endpoint: string, yes: boolean) {
  const endsWithSlash = endpoint.endsWith('/');
  if (yes && !endsWithSlash) {
    return `${endpoint}/`;
  }
  if (!yes && endsWithSlash) {
    return endpoint.slice(0, endpoint.length - 1);
  }
  return endpoint;
}
